/**
 * BaseFlavor — shared infrastructure for all flavor modules.
 *
 * ┌──────────────────────────────────────────────────────────────┐
 * │              UNIVERSAL MESSAGE CONTRACT                      │
 * │                                                              │
 * │  Backend → Widget (incoming):                                │
 * │    show_items     Display item cards (products/rooms/etc.)   │
 * │    show_products  Same as show_items (e-commerce backend)    │
 * │    cart_updated   Update order/cart/booking state             │
 * │    show_media     Show photo gallery                         │
 * │    dismiss_media  Close gallery                              │
 * │                                                              │
 * │  Widget → Backend (outgoing):                                │
 * │    product_selected    User tapped an item                   │
 * │    product_deselected  User removed an item                  │
 * │                                                              │
 * │  These 6 types are the contract between widget and backend.  │
 * │  Flavors may add extra handlers but must NOT rename these.   │
 * └──────────────────────────────────────────────────────────────┘
 *
 * BaseFlavor wires all universal handlers automatically. Subclasses only:
 *  - createManager(opts)  → domain manager instance
 *  - getManagerHideMethod() → name of manager's hide method
 *  - buildExtraMessageHandlers() → additional domain-specific handlers (optional)
 *  - buildTools() → client tools (optional)
 *
 * Manager contract: every manager must implement:
 *  - handleShowItems(message)
 *  - handleCartUpdated(message)
 *  - [hideMethod]() — the method returned by getManagerHideMethod()
 */
import { createGalleryHandler } from '../widget/galleryHandler.js';
import { createRecipeHandler } from '../ecommerce/RecipeHandler.js';

/**
 * The universal message types that form the contract between widget and backend.
 * Flavors receive these incoming types and send these outgoing types.
 * Additional flavor-specific types can be added via buildExtraMessageHandlers().
 */
export const MESSAGE_TYPES = Object.freeze({
  // Incoming (backend → widget)
  SHOW_ITEMS:    'show_items',
  CART_UPDATED:  'cart_updated',
  SHOW_MEDIA:    'show_media',
  DISMISS_MEDIA: 'dismiss_media',
  SHOW_RECIPE:    'show_recipe',
  DISMISS_RECIPE: 'dismiss_recipe',

  // Outgoing (widget → backend)
  PRODUCT_SELECTED:   'product_selected',
  PRODUCT_DESELECTED: 'product_deselected',
});

export class BaseFlavor {
  /**
   * @param {object} widget      — TTPChatWidget instance
   * @param {object} flavorOpts  — the flavor config object from user config
   * @param {object} [galleryOpts] — optional overrides for gallery (e.g. { buttonText, priceLabel })
   */
  constructor(widget, flavorOpts = {}, galleryOpts = {}) {
    this.widget = widget;
    this.flavorOpts = flavorOpts;
    this.galleryHandler = createGalleryHandler(widget, galleryOpts);

    const primaryColor =
      widget.config?.colors?.primary ||
      widget._rawConfig?.colors?.primary ||
      '#2563eb';

    // Let subclass create its domain manager
    this.manager = this.createManager({ shadowRoot: widget.shadowRoot, primaryColor });

    // Validate manager contract
    this._validateManager();

    // Assemble the public contract
    this.tools = this.buildTools();
    this.messageHandlers = this._assembleMessageHandlers();
  }

  // ─── Subclass hooks (override these) ──────────────────────────

  /**
   * Create the domain-specific manager (EcommerceManager, RestaurantManager, etc.)
   * Manager must implement: handleShowItems(msg), handleCartUpdated(msg), and the hide method.
   * @param {{ shadowRoot, primaryColor }} opts
   * @returns {object} manager instance
   */
  createManager(/* opts */) {
    throw new Error('BaseFlavor subclass must implement createManager()');
  }

  /**
   * The method name on the manager that hides its UI (called on dismiss/cleanup).
   * e.g. 'hideProducts', 'hideMenuItems', 'hideRooms'
   * @returns {string}
   */
  getManagerHideMethod() {
    return 'hideProducts';
  }

  /**
   * Return additional domain-specific message handlers beyond the universal set.
   * Example: ecommerce adds 'add_to_store_cart' and 'get_store_cart'.
   *
   * Do NOT override universal types here — they are wired automatically.
   * If you return a key that conflicts with a universal type, it will be ignored
   * and a warning will be logged.
   *
   * @returns {{ [messageType: string]: function }}
   */
  buildExtraMessageHandlers() {
    return {};
  }

  /**
   * Return client tools array.
   * @returns {Array<{ name: string, handler: function }>}
   */
  buildTools() {
    return [];
  }

  _getRecipeHandler() {
    if (!this._recipeHandler) {
      this._recipeHandler = createRecipeHandler(this.widget);
    }
    return this._recipeHandler;
  }

  // ─── Shared helpers (available to subclasses) ─────────────────

  /**
   * Send a message to the voice SDK WebSocket.
   */
  sendToVoiceSDK(message) {
    try {
      const voiceSDK = this.widget.voiceInterface?.sdk?.voiceSDK;
      if (voiceSDK?.sendMessage) voiceSDK.sendMessage(message);
    } catch (e) {
      console.warn(`[${this.constructor.name}] Could not send to VoiceSDK:`, e);
    }
  }

  /**
   * Stop any current audio playback (best-effort).
   * Call before sending product_selected so the agent stops talking.
   */
  stopPlayback() {
    try {
      const voiceSDK = this.widget.voiceInterface?.sdk?.voiceSDK;
      if (voiceSDK?.stopAudioPlayback) voiceSDK.stopAudioPlayback();
    } catch (e) { /* best-effort */ }
  }

  /**
   * Standard handler for item/product selection.
   * Stops playback and sends product_selected to backend.
   */
  handleItemSelected(item) {
    this.stopPlayback();
    this.sendToVoiceSDK({
      t: MESSAGE_TYPES.PRODUCT_SELECTED,
      productId: item.id,
      productName: item.name,
      price: item.price,
      quantity: item.selectedAmount || item.selectedNights || 1,
      sellBy: item.sellBy || 'quantity',
    });
  }

  /**
   * Standard handler for item deselection.
   */
  handleItemDeselected(item) {
    this.sendToVoiceSDK({
      t: MESSAGE_TYPES.PRODUCT_DESELECTED,
      productId: item.id,
      productName: item.name,
    });
  }

  /**
   * Hide the manager's UI.
   */
  hideManagerUI() {
    const method = this.getManagerHideMethod();
    if (this.manager && typeof this.manager[method] === 'function') {
      this.manager[method]();
    }
  }

  // ─── Internal wiring ──────────────────────────────────────────

  /**
   * Validate that the manager implements the required contract methods.
   */
  _validateManager() {
    const m = this.manager;
    if (!m) return;

    const hideMethod = this.getManagerHideMethod();
    const required = ['handleShowItems', 'handleCartUpdated', hideMethod];

    for (const method of required) {
      if (typeof m[method] !== 'function') {
        console.error(
          `[${this.constructor.name}] Manager is missing required method: ${method}(). ` +
          `All managers must implement: handleShowItems(), handleCartUpdated(), ${hideMethod}().`
        );
      }
    }
  }

  /**
   * Build the complete message handler map:
   *  1. Universal handlers (always present, structurally enforced)
   *  2. Extra handlers from subclass (domain-specific additions)
   */
  _assembleMessageHandlers() {
    const routeShowItems = (msg) => {
      this.galleryHandler.dismissGallery();
      this.manager.handleShowItems(msg);
    };

    // Universal handlers — these are the contract
    const universal = {
      [MESSAGE_TYPES.SHOW_ITEMS]: routeShowItems,
      // Backend / ClientToolCallManager often sends t: "show_products" (e-commerce); same payload shape as show_items for EcommerceManager
      show_products: routeShowItems,
      [MESSAGE_TYPES.CART_UPDATED]: (msg) => {
        this.manager.handleCartUpdated(msg);
      },
      [MESSAGE_TYPES.SHOW_MEDIA]: (msg) => {
        this.hideManagerUI();
        this.galleryHandler.handleShowMedia(msg);
      },
      [MESSAGE_TYPES.DISMISS_MEDIA]: () => {
        this.galleryHandler.dismissGallery();
      },
      [MESSAGE_TYPES.SHOW_RECIPE]: (msg) => {
        this._getRecipeHandler().handleShowRecipe(msg);
      },
      [MESSAGE_TYPES.DISMISS_RECIPE]: (msg) => {
        this._getRecipeHandler().dismissRecipe({
          animate: msg?.silent !== true && msg?.animate !== false,
        });
      },
    };

    // Extra handlers from subclass
    const extra = this.buildExtraMessageHandlers();

    // Warn if subclass tries to override universal types
    const universalKeys = new Set(Object.values(MESSAGE_TYPES));
    for (const key of Object.keys(extra)) {
      if (universalKeys.has(key)) {
        console.warn(
          `[${this.constructor.name}] buildExtraMessageHandlers() returned universal type "${key}" — ` +
          `this will be ignored. Universal handlers are managed by BaseFlavor.`
        );
        delete extra[key];
      }
    }

    return { ...universal, ...extra };
  }

  /**
   * Hook call-end to clean up UI.
   * Called by TTPChatWidget._initFlavor().
   */
  hookCallEnd() {
    const vi = this.widget.voiceInterface;
    if (!vi) return;
    const originalOnCallEnd = vi.config.onCallEnd;
    const self = this;
    vi.config.onCallEnd = function () {
      self.hideManagerUI();
      self.galleryHandler.dismissGallery();
      if (originalOnCallEnd) originalOnCallEnd();
    };
  }

  /**
   * Clean up resources.
   */
  destroy() {
    if (this.manager?.destroy) this.manager.destroy();
    this.galleryHandler.dismissGallery();
  }
}
